import { useState, useEffect } from 'react';
import { apiGet, apiPatch } from '../lib/api';
import { toast } from '../lib/toast';

interface Service {
  id: number;
  name: string;
  duration_min: number;
  price_cents: number;
}

interface Props {
  bookingId: number;
  currentServiceId: number;
  currentStartAt: string;   // "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DD HH:MM"
  onClose: () => void;
  onSaved: () => void;       // parent reloads after save
}

export function EditAppointmentModal({ bookingId, currentServiceId, currentStartAt, onClose, onSaved }: Props) {
  const [services, setServices] = useState<Service[] | null>(null);
  const [serviceId, setServiceId] = useState<number>(currentServiceId);
  const [date, setDate] = useState<string>(currentStartAt.slice(0, 10));
  const [time, setTime] = useState<string>(currentStartAt.slice(11, 16));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet<{ services: Service[] }>('/api/services')
      .then((r) => setServices(r.services))
      .catch((e) => toast.error('Failed to load services: ' + (e as Error).message));
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const patch: Record<string, unknown> = {};
      if (serviceId !== currentServiceId) patch.service_id = serviceId;
      const newStart = `${date} ${time}:00`;
      // Compare against the canonical (with-seconds) form of the original.
      const originalNormalized = currentStartAt.length === 16
        ? `${currentStartAt}:00`
        : currentStartAt;
      if (newStart !== originalNormalized) patch.start_at = newStart;
      if (Object.keys(patch).length === 0) {
        toast.success('No changes');
        onClose();
        return;
      }
      await apiPatch(`/api/bookings/${bookingId}`, patch);
      toast.success('Appointment updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        className="card max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Edit appointment</h2>

        <div>
          <label className="label">Service</label>
          {services === null ? (
            <div className="text-sm text-muted">Loading services…</div>
          ) : (
            <select
              className="input w-full"
              value={serviceId}
              onChange={(e) => setServiceId(Number(e.target.value))}
            >
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.duration_min}min · ${(s.price_cents / 100).toFixed(2)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Date</label>
            <input
              type="date"
              className="input w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">Time</label>
            <input
              type="time"
              className="input w-full"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost px-3 py-2" disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary px-3 py-2" disabled={busy || services === null}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
